import React, { useEffect, useState, useCallback } from "react";
import { Truck, RefreshCw, CheckCircle2, Package, AlertTriangle, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || '';

const authFetch = (path, opts = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(`${API}${path}`, {
    ...opts,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
};

const LEVELS = ["MAIL", "PRIORITY_MAIL", "GROUND", "EXPEDITED", "EXPRESS"];

const AdminFulfillment = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({ awaiting: 0, submitted: 0 });
  const [tab, setTab] = useState("paid_awaiting_fulfillment");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [levels, setLevels] = useState({});
  const [toast, setToast] = useState(null);
  const [editAddr, setEditAddr] = useState(null);
  const [addrForm, setAddrForm] = useState({});
  const [pickupWins, setPickupWins] = useState([]);
  const [rsSel, setRsSel] = useState({});

  useEffect(() => {
    authFetch("/api/payments/pickup/windows")
      .then((r) => r.json())
      .then((d) => setPickupWins(d.windows || []))
      .catch(() => {});
  }, []);

  const markPickedUp = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await authFetch(`/api/admin/fulfillment/orders/${id}/mark-picked-up`, { method: "POST" });
      if (!r.ok) throw new Error();
      flash("ok", "Marked as picked up");
      load();
    } catch { flash("err", "Could not mark picked up"); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const reschedulePickup = async (id) => {
    const wid = rsSel[id];
    if (!wid) { flash("err", "Choose a new window first"); return; }
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await authFetch(`/api/admin/fulfillment/orders/${id}/reschedule`, {
        method: "POST", body: JSON.stringify({ window_id: wid }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "");
      flash("ok", `Rescheduled → ${d.pickup_window_label || ""}`);
      load();
    } catch (e) { flash("err", typeof e.message === "string" && e.message ? e.message : "Could not reschedule"); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`/api/admin/fulfillment/orders?status=${tab}`);
      if (r.status === 401) { navigate("/login"); return; }
      const d = await r.json();
      setOrders(d.orders || []);
      setCounts(d.counts || { awaiting: 0, submitted: 0 });
    } catch (e) {
      setToast({ type: "err", msg: "Failed to load orders" });
    } finally {
      setLoading(false);
    }
  }, [tab, navigate]);

  useEffect(() => { load(); }, [load]);

  const flash = (type, msg) => { setToast({ type, msg }); setTimeout(() => setToast(null), 4000); };

  const sendToLulu = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await authFetch(`/api/admin/fulfillment/orders/${id}/send-to-lulu`, {
        method: "POST",
        body: JSON.stringify({ shipping_level: levels[id] || "MAIL" }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = d.detail;
        throw new Error(typeof detail === "string" ? detail : (detail?.message || "Lulu rejected the job"));
      }
      flash("ok", `Sent to Lulu — job #${d.lulu_job_id}`);
      load();
    } catch (e) {
      flash("err", e.message);
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const sendAll = async () => {
    if (!window.confirm("Send ALL awaiting orders to Lulu now?")) return;
    setBulkBusy(true);
    try {
      const r = await authFetch(`/api/admin/fulfillment/send-all`, {
        method: "POST",
        body: JSON.stringify({ shipping_level: "MAIL" }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Bulk send failed");
      const s = d.summary || {};
      flash("ok", `Sent ${s.sent || 0} to Lulu · ${s.failed || 0} failed · ${s.skipped || 0} skipped`);
      load();
    } catch (e) {
      flash("err", e.message);
    } finally {
      setBulkBusy(false);
    }
  };

  const refresh = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await authFetch(`/api/admin/fulfillment/orders/${id}/refresh`, { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Could not refresh");
      flash("ok", `Lulu status: ${d.lulu_status || "unknown"}`);
      load();
    } catch (e) {
      flash("err", e.message);
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const openAddr = (o) => {
    const a = o.lulu_address || o.shipping_address || {};
    setAddrForm({
      name: a.name || o.customer?.name || "",
      street1: a.street1 || "", street2: a.street2 || "",
      city: a.city || "", state_code: a.state_code || "",
      postcode: a.postcode || "", country_code: a.country_code || "US",
    });
    setEditAddr(o.order_id);
  };

  const applySuggested = (o) => {
    const s = o.lulu_suggested_address || {};
    setAddrForm((f) => ({
      ...f,
      street1: s.street1 || f.street1, street2: s.street2 || f.street2 || "",
      city: s.city || f.city, state_code: s.state_code || f.state_code,
      postcode: s.postcode || f.postcode, country_code: s.country_code || f.country_code,
    }));
  };

  const saveAddr = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await authFetch(`/api/admin/fulfillment/orders/${id}/address`, {
        method: "POST", body: JSON.stringify(addrForm),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not update address");
      flash("ok", "Address updated");
      setEditAddr(null);
      load();
    } catch (e) {
      flash("err", e.message);
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const resubmit = async (id) => {
    if (!window.confirm("Cancel the current Lulu job and resubmit with the corrected address?")) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await authFetch(`/api/admin/fulfillment/orders/${id}/resubmit`, {
        method: "POST", body: JSON.stringify({ shipping_level: levels[id] || "MAIL" }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : (d.detail?.message || "Resubmit failed"));
      flash("ok", `Resubmitted — new Lulu job #${d.lulu_job_id}`);
      load();
    } catch (e) {
      flash("err", e.message);
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const saveAndResubmit = async (id) => {
    if (!window.confirm("Save this address, cancel the current Lulu job, and resubmit?")) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      let r = await authFetch(`/api/admin/fulfillment/orders/${id}/address`, {
        method: "POST", body: JSON.stringify(addrForm),
      });
      let d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not update address");
      r = await authFetch(`/api/admin/fulfillment/orders/${id}/resubmit`, {
        method: "POST", body: JSON.stringify({ shipping_level: levels[id] || "MAIL" }),
      });
      d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : (d.detail?.message || "Resubmit failed"));
      flash("ok", `Address fixed & resubmitted — new Lulu job #${d.lulu_job_id}`);
      setEditAddr(null);
      load();
    } catch (e) {
      flash("err", e.message);
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const money = (v) => (typeof v === "number" ? `$${v.toFixed(2)}` : "—");
  const addr = (a) => (a ? `${a.street1 || ""}${a.street2 ? " " + a.street2 : ""}, ${a.city || ""} ${a.state_code || ""} ${a.postcode || ""}` : "—");
  const shipCost = (v) => (typeof v === "number" ? (v === 0 ? " — FREE" : ` — $${v.toFixed(2)}`) : "");
  const fmt = (d) => (d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
  const stageStyle = (s) => {
    const map = {
      "Order received": { c: "#e8c87a", b: "rgba(212,162,84,0.12)", br: "rgba(212,162,84,0.35)" },
      "In production & fulfillment": { c: "#7aa7d4", b: "rgba(122,167,212,0.12)", br: "rgba(122,167,212,0.35)" },
      "Shipped": { c: "#8fbf8f", b: "rgba(143,191,143,0.12)", br: "rgba(143,191,143,0.35)" },
      "Delivered": { c: "#8fbf8f", b: "rgba(143,191,143,0.2)", br: "rgba(143,191,143,0.55)" },
      "Needs attention": { c: "#e07a5f", b: "rgba(224,122,95,0.12)", br: "rgba(224,122,95,0.4)" },
      "Awaiting payment": { c: "rgba(232,226,212,0.5)", b: "transparent", br: "rgba(232,226,212,0.2)" },
    };
    const v = map[s] || map["Order received"];
    return { color: v.c, background: v.b, border: `1px solid ${v.br}` };
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0b0a0f", color: "#e8e2d4", fontFamily: "Inter, system-ui, sans-serif" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 24px" }}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 600, color: "#e8c87a", letterSpacing: "0.5px" }} data-testid="fulfillment-title">
              <Package size={22} style={{ display: "inline", marginRight: 10, marginTop: -4 }} />
              Fulfillment Console
            </h1>
            <p style={{ color: "rgba(232,226,212,0.55)", fontSize: 13, marginTop: 4 }}>Book of Colorz — push paid orders to Lulu print-on-demand</p>
          </div>
          <button onClick={() => { logout(); navigate("/login"); }} data-testid="fulfillment-logout"
            style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "1px solid rgba(212,162,84,0.35)", color: "rgba(232,226,212,0.8)", padding: "8px 16px", borderRadius: 999, fontSize: 13, cursor: "pointer" }}>
            <LogOut size={15} /> Log out
          </button>
        </div>

        <div className="flex gap-3 mb-6">
          {[
            { k: "paid_awaiting_fulfillment", label: `Awaiting (${counts.awaiting})` },
            { k: "submitted_to_lulu", label: `Submitted (${counts.submitted})` },
            { k: "paid_awaiting_pickup", label: `Pickup (${counts.awaiting_pickup || 0})` },
            { k: "all", label: "All paid" },
          ].map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`tab-${t.k}`}
              style={{ padding: "8px 18px", borderRadius: 999, fontSize: 13, cursor: "pointer",
                border: `1px solid ${tab === t.k ? "#d4a254" : "rgba(212,162,84,0.2)"}`,
                background: tab === t.k ? "rgba(212,162,84,0.12)" : "transparent",
                color: tab === t.k ? "#e8c87a" : "rgba(232,226,212,0.6)" }}>
              {t.label}
            </button>
          ))}
          <button onClick={load} data-testid="reload-btn" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", color: "rgba(232,226,212,0.6)", cursor: "pointer", fontSize: 13 }}>
            <RefreshCw size={15} /> Reload
          </button>
          {counts.awaiting > 0 && (
            <button onClick={sendAll} disabled={bulkBusy} data-testid="send-all-btn"
              style={{ display: "flex", alignItems: "center", gap: 7, background: "linear-gradient(90deg,#d4a254,#e8c87a)", color: "#1a1520", padding: "8px 18px", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: bulkBusy ? 0.6 : 1, border: "none" }}>
              <Truck size={15} /> {bulkBusy ? "Sending all…" : `Send all awaiting (${counts.awaiting}) to Lulu`}
            </button>
          )}
        </div>

        <div data-testid="fulfillment-legend" style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 12, color: "rgba(232,226,212,0.5)", marginBottom: 18, padding: "10px 16px", border: "1px solid rgba(212,162,84,0.12)", borderRadius: 10 }}>
          <span><b style={{ color: "rgba(232,226,212,0.72)" }}>Awaiting</b>: paid, not yet sent to Lulu</span>
          <span><b style={{ color: "rgba(232,226,212,0.72)" }}>Submitted</b>: print job created at Lulu</span>
          <span><b style={{ color: "rgba(232,226,212,0.72)" }}>All paid</b>: every paid order</span>
          <span style={{ width: "100%", color: "rgba(232,226,212,0.42)", marginTop: 2 }}>Shipping matrix → Lulu: Standard/Free = <b style={{ color: "#d4a254" }}>MAIL</b> · Ground = <b style={{ color: "#d4a254" }}>GROUND</b> · Priority = <b style={{ color: "#d4a254" }}>PRIORITY_MAIL</b> · Expedited = <b style={{ color: "#d4a254" }}>EXPEDITED</b> · Express = <b style={{ color: "#d4a254" }}>EXPRESS</b> <span style={{ color: "rgba(232,226,212,0.3)" }}>(auto-selected per order from what the customer paid)</span></span>
        </div>

        {counts.color_forward > 0 && (
          <div data-testid="color-forward-summary" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", borderRadius: 12, marginBottom: 18, background: "rgba(143,191,143,0.08)", border: "1px solid rgba(143,191,143,0.3)" }}>
            <span style={{ fontSize: 18 }}>🌱</span>
            <span style={{ fontSize: 13.5, color: "#c9e0c9" }}>
              <b style={{ color: "#8fbf8f" }}>{counts.color_forward} Color Forward gift(s)</b> pledged
              {" "}({counts.color_forward_anonymous} anonymous · {counts.color_forward_named} named) —
              {" "}when this hits your bulk threshold, place one gifted print run to fulfill them together.
            </span>
          </div>
        )}

        {loading ? (
          <p style={{ color: "rgba(232,226,212,0.5)", padding: 40, textAlign: "center" }} data-testid="fulfillment-loading">Loading orders…</p>
        ) : orders.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, border: "1px dashed rgba(212,162,84,0.2)", borderRadius: 14 }} data-testid="fulfillment-empty">
            <CheckCircle2 size={32} style={{ color: "#8fbf8f", marginBottom: 12 }} />
            <p style={{ color: "rgba(232,226,212,0.6)" }}>No orders in this view.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="orders-list">
            {orders.map((o) => {
              const jobId = o.lulu_job?.id || o.lulu_job?.body?.id;
              const sent = !!jobId;
              return (
                <div key={o.order_id} data-testid={`order-row-${o.order_id.slice(0, 8)}`}
                  style={{ border: "1px solid rgba(212,162,84,0.15)", borderRadius: 14, padding: "18px 20px", background: "rgba(255,255,255,0.02)" }}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div style={{ minWidth: 260, flex: 1 }}>
                      <div className="flex items-center gap-2 mb-1" style={{ flexWrap: "wrap" }}>
                        <span style={{ fontFamily: "monospace", fontSize: 13, color: "#e8c87a" }}>#{o.order_id.slice(0, 8).toUpperCase()}</span>
                        <span style={{ fontSize: 12, color: "rgba(232,226,212,0.4)" }}>· {o.quantity} book(s) · {money(o.total)}</span>
                        {o.color_forward && (
                          <span data-testid={`cf-badge-${o.order_id.slice(0, 8)}`} style={{ fontSize: 11, color: "#8fbf8f", background: "rgba(143,191,143,0.12)", border: "1px solid rgba(143,191,143,0.35)", padding: "2px 9px", borderRadius: 999 }}>
                            🌱 Color Forward · {o.color_forward}
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: 14, color: "#e8e2d4" }}>{o.customer?.name || "—"} <span style={{ color: "rgba(232,226,212,0.5)" }}>· {o.customer?.email || "—"}</span></p>
                      {o.fulfillment_channel === "LOCAL" ? (
                        <div data-testid={`pickup-info-${o.order_id.slice(0, 8)}`} style={{ marginTop: 4 }}>
                          <p style={{ fontSize: 13, color: "#e8c87a" }}>🎟️ Local Pickup · {o.pickup_window_label || "—"}</p>
                          <p style={{ fontSize: 12, color: "rgba(232,226,212,0.55)", marginTop: 2 }}>📍 {o.pickup_location || "Boxyard RTP"}</p>
                          {o.customer?.phone && (<p style={{ fontSize: 12, color: "rgba(232,226,212,0.5)", marginTop: 2 }}>☎ {o.customer.phone}</p>)}
                        </div>
                      ) : (
                        <>
                          <p style={{ fontSize: 13, color: "rgba(232,226,212,0.6)", marginTop: 2 }}>{addr(o.shipping_address)}</p>
                          <p style={{ fontSize: 12, color: "rgba(232,226,212,0.45)", marginTop: 4 }} data-testid={`shipchoice-${o.order_id.slice(0, 8)}`}>
                            Customer chose: <span style={{ color: "rgba(232,226,212,0.75)" }}>{o.shipping_label || "—"}{shipCost(o.shipping_cost)}</span>
                            <span style={{ color: "rgba(232,226,212,0.35)" }}>{"  →  Lulu: "}</span><span style={{ color: "#d4a254" }}>{o.suggested_lulu_level}</span>
                          </p>
                        </>
                      )}
                      <span data-testid={`stage-${o.order_id.slice(0, 8)}`} style={{ display: "inline-block", marginTop: 8, fontSize: 11, padding: "3px 11px", borderRadius: 999, ...stageStyle(o.stage) }}>{o.stage}</span>

                      {o.stage === "Needs attention" && (
                        <div style={{ marginTop: 6, fontSize: 11, color: "#e07a5f" }} data-testid={`reason-${o.order_id.slice(0, 8)}`}>
                          ⚠ {o.lulu_reason || "Rejected by Lulu — click Refresh status for the detailed reason"}
                        </div>
                      )}
                      {!o.has_address && o.payment_status === "paid" && !o.lulu_job_id && o.fulfillment_channel !== "LOCAL" && (
                        <div style={{ marginTop: 6, fontSize: 11, color: "#e07a5f" }} data-testid={`noaddr-${o.order_id.slice(0, 8)}`}>
                          ⚠ No shipping address on this order (created before address capture) — cannot fulfill.
                        </div>
                      )}

                      <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid rgba(212,162,84,0.08)", fontSize: 11, color: "rgba(232,226,212,0.4)", lineHeight: 1.8 }} data-testid={`ids-${o.order_id.slice(0, 8)}`}>
                        <div>Correlation ID: <span style={{ fontFamily: "monospace", color: "rgba(232,226,212,0.6)" }}>{o.order_id}</span></div>
                        <div>A · Stripe: {o.session_id ? (<a href={o.stripe_url} target="_blank" rel="noopener noreferrer" style={{ color: "#7aa7d4", fontFamily: "monospace" }} data-testid={`stripe-link-${o.order_id.slice(0, 8)}`}>{o.session_id.slice(0, 24)}… ↗</a>) : "—"}</div>
                        <div>C · Lulu: <span style={{ fontFamily: "monospace", color: "rgba(232,226,212,0.6)" }}>{o.lulu_job_id ? `#${o.lulu_job_id}` : "—"}</span></div>
                        <div>Created {fmt(o.created_at)} · Paid {fmt(o.paid_at)}{o.lulu_submitted_at ? ` · Sent to Lulu ${fmt(o.lulu_submitted_at)}` : ""}</div>
                      </div>
                    </div>

                    <div style={{ textAlign: "right", minWidth: 220 }}>
                      {o.fulfillment_channel === "LOCAL" ? (
                        <div className="flex flex-col items-end gap-2" data-testid={`pickup-actions-${o.order_id.slice(0, 8)}`}>
                          {o.fulfillment_status === "picked_up" ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#8fbf8f", fontSize: 13 }}><CheckCircle2 size={15} /> Picked up</span>
                          ) : (
                            <button onClick={() => markPickedUp(o.order_id)} disabled={busy[o.order_id]} data-testid={`mark-pickup-${o.order_id.slice(0, 8)}`}
                              style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "linear-gradient(90deg,#d4a254,#e8c87a)", color: "#1a1520", padding: "9px 18px", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer", border: "none", opacity: busy[o.order_id] ? 0.6 : 1 }}>
                              <CheckCircle2 size={14} /> Mark picked up
                            </button>
                          )}
                          <div className="flex items-center gap-2">
                            <select value={rsSel[o.order_id] || ""} onChange={(e) => setRsSel((s) => ({ ...s, [o.order_id]: e.target.value }))} data-testid={`resched-sel-${o.order_id.slice(0, 8)}`}
                              style={{ background: "#15131b", color: "#e8e2d4", border: "1px solid rgba(212,162,84,0.25)", borderRadius: 8, padding: "6px 10px", fontSize: 12 }}>
                              <option value="">Reschedule to…</option>
                              {pickupWins.map((w) => (<option key={w.window_id} value={w.window_id} disabled={w.sold_out}>{w.label}{w.sold_out ? " (full)" : ` (${w.remaining} left)`}</option>))}
                            </select>
                            <button onClick={() => reschedulePickup(o.order_id)} disabled={busy[o.order_id]} data-testid={`resched-btn-${o.order_id.slice(0, 8)}`}
                              style={{ background: "transparent", border: "1px solid rgba(212,162,84,0.3)", color: "rgba(232,226,212,0.8)", padding: "6px 12px", borderRadius: 999, fontSize: 12, cursor: "pointer" }}>Move</button>
                          </div>
                        </div>
                      ) : sent ? (
                        <div>
                          <span data-testid={`status-${o.order_id.slice(0, 8)}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#8fbf8f", fontSize: 13 }}>
                            <CheckCircle2 size={15} /> Sent to Lulu · #{jobId}
                          </span>
                          <p style={{ fontSize: 12, color: "rgba(232,226,212,0.5)", marginTop: 4 }}>{o.lulu_last_status ? `Status: ${o.lulu_last_status}` : (o.lulu_job?.shipping_level || "")}</p>
                          {o.tracking && (
                            <div data-testid={`tracking-${o.order_id.slice(0, 8)}`} style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "rgba(143,191,143,0.08)", border: "1px solid rgba(143,191,143,0.3)", textAlign: "left" }}>
                              <div style={{ fontSize: 11, color: "#8fbf8f", fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}><Truck size={12} /> {o.tracking.carrier || "Carrier"} tracking</div>
                              <div style={{ fontSize: 11.5, color: "rgba(232,226,212,0.8)", fontFamily: "monospace", marginTop: 3, wordBreak: "break-all" }}>{o.tracking.id || "—"}</div>
                              {Array.isArray(o.tracking.urls) && o.tracking.urls[0] && (
                                <a href={o.tracking.urls[0]} target="_blank" rel="noopener noreferrer" data-testid={`track-link-${o.order_id.slice(0, 8)}`} style={{ fontSize: 11, color: "#7aa7d4" }}>Track package ↗</a>
                              )}
                              {o.tracking.estimated?.arrival_min && (
                                <div style={{ fontSize: 10.5, color: "rgba(232,226,212,0.45)", marginTop: 3 }}>Est. arrival {o.tracking.estimated.arrival_min} – {o.tracking.estimated.arrival_max}</div>
                              )}
                            </div>
                          )}
                          <div className="flex flex-col items-end gap-2" style={{ marginTop: 8 }}>
                            <button onClick={() => refresh(o.order_id)} disabled={busy[o.order_id]} data-testid={`refresh-${o.order_id.slice(0, 8)}`}
                              style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "1px solid rgba(212,162,84,0.3)", color: "rgba(232,226,212,0.75)", padding: "6px 14px", borderRadius: 999, fontSize: 12, cursor: "pointer" }}>
                              <RefreshCw size={13} /> {busy[o.order_id] ? "…" : "Refresh status"}
                            </button>
                            <button onClick={() => openAddr(o)} data-testid={`fixaddr-${o.order_id.slice(0, 8)}`}
                              style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "1px solid rgba(224,122,95,0.4)", color: "#e6a08c", padding: "6px 14px", borderRadius: 999, fontSize: 12, cursor: "pointer" }}>
                              <AlertTriangle size={13} /> Fix address & resubmit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-end gap-2">
                          <select value={levels[o.order_id] || o.suggested_lulu_level || "MAIL"} onChange={(e) => setLevels((l) => ({ ...l, [o.order_id]: e.target.value }))} data-testid={`level-${o.order_id.slice(0, 8)}`}
                            style={{ background: "#15131b", color: "#e8e2d4", border: "1px solid rgba(212,162,84,0.25)", borderRadius: 8, padding: "6px 10px", fontSize: 12 }}>
                            {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                          </select>
                          <button onClick={() => sendToLulu(o.order_id)} disabled={busy[o.order_id]} data-testid={`send-${o.order_id.slice(0, 8)}`}
                            style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "linear-gradient(90deg,#d4a254,#e8c87a)", color: "#1a1520", padding: "9px 20px", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: busy[o.order_id] ? 0.6 : 1, border: "none" }}>
                            <Truck size={15} /> {busy[o.order_id] ? "Sending…" : "Send to Lulu"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {editAddr === o.order_id && (
                    <div data-testid={`addr-editor-${o.order_id.slice(0, 8)}`} style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(212,162,84,0.15)" }}>
                      <div style={{ fontSize: 12, color: "#e8c87a", fontWeight: 600, marginBottom: 8 }}>Correct shipping address</div>
                      {o.lulu_suggested_address && (
                        <div style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 8, background: "rgba(122,167,212,0.08)", border: "1px solid rgba(122,167,212,0.3)" }}>
                          <div style={{ fontSize: 11, color: "#7aa7d4", marginBottom: 4 }}>Lulu recommends:</div>
                          <div style={{ fontSize: 12, color: "rgba(232,226,212,0.8)" }}>
                            {o.lulu_suggested_address.street1}{o.lulu_suggested_address.street2 ? `, ${o.lulu_suggested_address.street2}` : ""}, {o.lulu_suggested_address.city} {o.lulu_suggested_address.state_code} {o.lulu_suggested_address.postcode}
                          </div>
                          <button onClick={() => applySuggested(o)} data-testid={`apply-suggested-${o.order_id.slice(0, 8)}`}
                            style={{ marginTop: 6, background: "transparent", border: "1px solid rgba(122,167,212,0.5)", color: "#7aa7d4", padding: "4px 12px", borderRadius: 999, fontSize: 11, cursor: "pointer" }}>
                            Use Lulu's recommended address
                          </button>
                        </div>
                      )}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8 }}>
                        {[
                          ["name", "Name"], ["street1", "Street 1"], ["street2", "Street 2"],
                          ["city", "City"], ["state_code", "State"], ["postcode", "Postal code"], ["country_code", "Country"],
                        ].map(([k, label]) => (
                          <input key={k} value={addrForm[k] || ""} placeholder={label}
                            onChange={(e) => setAddrForm((f) => ({ ...f, [k]: e.target.value }))}
                            data-testid={`addr-${k}-${o.order_id.slice(0, 8)}`}
                            style={{ background: "#15131b", color: "#e8e2d4", border: "1px solid rgba(212,162,84,0.25)", borderRadius: 8, padding: "7px 10px", fontSize: 12 }} />
                        ))}
                      </div>
                      <div className="flex gap-2" style={{ marginTop: 10, flexWrap: "wrap" }}>
                        <button onClick={() => saveAddr(o.order_id)} disabled={busy[o.order_id]} data-testid={`save-addr-${o.order_id.slice(0, 8)}`}
                          style={{ background: "transparent", border: "1px solid rgba(212,162,84,0.4)", color: "#e8c87a", padding: "7px 16px", borderRadius: 999, fontSize: 12, cursor: "pointer" }}>
                          Save address only
                        </button>
                        <button onClick={() => saveAndResubmit(o.order_id)} disabled={busy[o.order_id]} data-testid={`save-resubmit-${o.order_id.slice(0, 8)}`}
                          style={{ background: "linear-gradient(90deg,#d4a254,#e8c87a)", color: "#1a1520", padding: "7px 16px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "none" }}>
                          <Truck size={13} style={{ display: "inline", marginRight: 5, marginTop: -2 }} />{busy[o.order_id] ? "Working…" : "Save & resubmit to Lulu"}
                        </button>
                        <button onClick={() => setEditAddr(null)} data-testid={`cancel-addr-${o.order_id.slice(0, 8)}`}
                          style={{ background: "transparent", border: "none", color: "rgba(232,226,212,0.5)", padding: "7px 12px", fontSize: 12, cursor: "pointer" }}>
                          Cancel
                        </button>
                      </div>
                      <p style={{ fontSize: 10.5, color: "rgba(232,226,212,0.4)", marginTop: 8 }}>
                        Note: Lulu can only cancel a job that is still UNPAID / pre-production. If it's already shipped or in production, cancel it in Lulu first.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {toast && (
        <div data-testid="fulfillment-toast" style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 50,
          display: "flex", alignItems: "center", gap: 8, padding: "12px 20px", borderRadius: 12, fontSize: 14,
          background: toast.type === "ok" ? "rgba(30,60,40,0.95)" : "rgba(70,30,30,0.95)",
          border: `1px solid ${toast.type === "ok" ? "#8fbf8f" : "#e07a5f"}`, color: "#fff", maxWidth: 520 }}>
          {toast.type === "ok" ? <CheckCircle2 size={17} /> : <AlertTriangle size={17} />}
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AdminFulfillment;
